import { UppyFile } from '@uppy/core';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IMAGE_ICON_PATH } from 'constants/images.constants';
import { useOrderContext } from 'hooks';
import { TranslationNamespace } from 'i18n';

type FilePreview =
  | { kind: 'image'; url: string; name: string }
  | { kind: 'file'; name: string };

export const SqPaymentReceiptsDispute: React.FC = () => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.payment_receipts_dispute',
  });

  const {
    receiptsResult: { uppy },
  } = useOrderContext();

  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<FilePreview | null>(null);
  const previewUrlRef = useRef<string | null>(null);

  useEffect(() => {
    const handler = (file: UppyFile) => {
      if (!(file.data instanceof File)) {
        return;
      }
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
      if (file.type?.startsWith('image/')) {
        const url = URL.createObjectURL(file.data);
        previewUrlRef.current = url;
        setPreview({ kind: 'image', url, name: file.name });
      } else {
        setPreview({ kind: 'file', name: file.name });
      }
    };

    uppy.on('file-added', handler);
    return () => {
      uppy.off('file-added', handler);
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
    };
  }, [uppy]);

  const addFiles = useCallback(
    (files: FileList | null) => {
      if (!files) {
        return;
      }
      Array.from(files).forEach((file) => {
        uppy.addFile({
          source: 'custom',
          name: file.name,
          type: file.type,
          data: file,
        });
      });
    },
    [uppy],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      addFiles(e.dataTransfer.files);
    },
    [addFiles],
  );

  return (
    <div className="tw-flex tw-flex-col tw-w-full tw-gap-3">
      <div
        className="tw-w-full tw-px-5 tw-py-4 tw-bg-payment-layout tw-rounded-2xl tw-flex tw-items-center tw-cursor-pointer"
        onClick={() => inputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
      >
        <input
          ref={inputRef}
          type="file"
          className="tw-hidden"
          onChange={(e) => addFiles(e.target.files)}
        />
        <img
          src={IMAGE_ICON_PATH}
          alt={t('buttons.sq_upload')}
          className="tw-mr-4"
        />
        <span className="tw-text-md">{t('buttons.sq_upload')}</span>
      </div>
      <span className="tw-text-md tw-text-secondary">
        {t('buttons.max_file_size', {
          size:
            ((uppy as any).opts.restrictions.maxFileSize ?? 0) / (1024 * 1024),
        })}
      </span>
      {preview && (
        <div className="tw-relative tw-mt-4 tw-w-16 tw-h-16">
          {preview.kind === 'image' ? (
            <img
              src={preview.url}
              alt={preview.name}
              className="tw-w-16 tw-h-16 tw-rounded-lg tw-object-cover"
            />
          ) : (
            <div className="tw-w-16 tw-h-16 tw-rounded-lg tw-bg-payment-layout tw-flex tw-items-center tw-justify-center">
              <span className="tw-text-xs tw-font-medium tw-text-secondary">
                {preview.name.includes('.')
                  ? `.${preview.name.split('.').pop()}`
                  : ''}
              </span>
            </div>
          )}
          <button
            type="button"
            aria-label="remove"
            className="tw-absolute -tw-top-2 -tw-right-2 tw-w-5 tw-h-5 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-bg-layout tw-border-none tw-cursor-pointer tw-shadow-[0_2px_4px_rgba(0,0,0,0.1)]"
            onClick={() => {
              uppy.cancelAll();
              setPreview(null);
            }}
          >
            <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
              <path
                d="M2 2l8 8M10 2L2 10"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};
